import { useState } from 'react';
import { Button } from '@/shared/ui';
import { Modal } from '@/shared/ui/Modal';
import {
  observationKindText,
  observationSummary,
  relativeTime,
} from '@/modules/agents/utils/agentStatusLabels';
import type { AgentObservation } from '../../api/agentStatusTypes';
import { useAgentObservations } from '../../hooks/useCommandCenter';
import styles from './AgentStatus.module.css';

/**
 * The agent's mission observations at one location, over the agent tab: data it read, events that
 * came in, and its own decisions, newest first. Each says whether the agent's next turn reads it as
 * evidence -- what it read is what it reasoned from (seen live: its own old "blocked" notes kept it
 * from working after an outage ended).
 */
export function ObservationsPanel({
  open,
  onClose,
  practiceId,
  locationId,
  roleKey,
  agentName,
  locationName,
}: {
  open: boolean;
  onClose: () => void;
  practiceId: string;
  locationId: string;
  roleKey: string;
  agentName: string;
  locationName: string;
}) {
  const query = useAgentObservations(practiceId, locationId, roleKey, open);
  const [onlyRead, setOnlyRead] = useState(false);
  const all = query.data?.pages.flatMap((page) => page.items) ?? [];
  const items = onlyRead ? all.filter((item) => item.readByAgent) : all;
  const now = Date.now();

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={`${agentName}'s observations`}
      variant="side"
      size="xl"
    >
      <div className={styles.runLog}>
        <p className={styles.quiet}>
          What {agentName} has recorded at {locationName}: data it read, events that came in, and
          its own decisions. Those marked “Read by {agentName}” are what its next turn reads as
          evidence.
        </p>
        {query.isLoading && <p className={styles.quiet}>Loading observations…</p>}
        {query.isError && <p className={styles.quiet}>Observations could not be loaded.</p>}
        {query.data && all.length === 0 && <p className={styles.quiet}>No observations yet.</p>}
        {all.length > 0 && (
          <div className={styles.factsToolbar}>
            <span>
              {all.filter((item) => item.readByAgent).length} of {all.length} shown are read by{' '}
              {agentName}
            </span>
            <label className={styles.toggle}>
              <input
                type="checkbox"
                checked={onlyRead}
                onChange={(event) => setOnlyRead(event.target.checked)}
              />
              Only what {agentName} reads
            </label>
          </div>
        )}
        {items.map((item) => (
          <ObservationEntry key={item.id} item={item} now={now} agentName={agentName} />
        ))}
        {query.hasNextPage && (
          <div className={styles.loadMore}>
            <Button
              variant="secondary"
              size="sm"
              disabled={query.isFetchingNextPage}
              onClick={() => query.fetchNextPage()}
            >
              {query.isFetchingNextPage ? 'Loading…' : 'Load older observations'}
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}

function ObservationEntry({
  item,
  now,
  agentName,
}: {
  item: AgentObservation;
  now: number;
  agentName: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className={styles.run}>
      <button
        type="button"
        className={styles.runSummary}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span className={styles.runLine}>
          <span className={styles.toneInfo}>{observationKindText(item.kind)}</span>
          <span className={item.readByAgent ? styles.toneSuccess : styles.toneMuted}>
            {item.readByAgent ? `Read by ${agentName}` : 'Not read'}
          </span>
          <span className={styles.mono}>{item.source}</span>
          <span title={new Date(item.observedAt).toLocaleString()}>
            {relativeTime(item.observedAt, now)}
          </span>
        </span>
        <span className={styles.factText}>{observationSummary(item.content)}</span>
      </button>
      {open && (
        <div className={styles.runDetail}>
          <pre className={styles.rawBlock}>{JSON.stringify(item.content, null, 2)}</pre>
          <span className={styles.mono}>
            {item.id}
            {item.runId ? ` · run ${item.runId}` : ''}
          </span>
        </div>
      )}
    </div>
  );
}
